import { useEffect, useRef, useState } from "react";
import classNames from "classnames/bind";
import debounce from "lodash/debounce";

import { ColourOption } from "@/components/Colours/types.ts";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { PlusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlusOutlined";
import { MinusOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/MinusOutlined";
import { Swatch } from "@/components/Colours/ColourList/Swatches/Swatch/Swatch.tsx";

import styles from "./Swatches.module.scss";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { COLOUR_SELECTOR_SHOW_MORE_BUTTON_CLICK } from "@/components/Colours/ColourList/monetateEvents.ts";

const cx = classNames.bind(styles);

export const SWATCH_ITEM_SIZE = 44;
export const GAP_SIZE = 12;
export const INNER_WRAPPER_PADDING = 12;

type Props = {
  /**
   * An array of colour option objects.
   */
  colourOptions: ColourOption[];
  /**
   * A boolean flag changing the colour selector behaviour for the dior brand
   */
  isDior: boolean;
  /**
   * A number representing the index of selected colour
   */
  selectedColourIndex: number;
  /**
   * A boolean flag to show the toggle button
   */
  isMobile?: boolean;
  /**
   * A boolean toggling history push with replace
   */
  replaceHistoryEntryOnVariantChange: boolean;
};

export const Swatches = ({
  isDior,
  colourOptions,
  selectedColourIndex,
  isMobile,
  replaceHistoryEntryOnVariantChange,
}: Props) => {
  const isLocalChange = useRef(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [visibleItemCount, setVisibleItemCount] = useState<
    number | undefined
  >();
  const [hideToggleButton, setHideToggleButton] = useState<boolean>(true);
  const coloursContainerRef = useRef<HTMLUListElement | null>(null);
  const activeElementRef = useRef<HTMLLIElement | null>(null);
  const skipLinkTargetRef = useRef<HTMLParagraphElement | null>(null);
  const [skipLinkTargetVisible, setSkipLinkTargetVisible] =
    useState<boolean>(false);
  const totalItems = colourOptions.length;

  const handleColourSelectClick = () => {
    isLocalChange.current = true;
  };

  const recalculateRows = () => {
    if (!coloursContainerRef.current) return;

    const rect = coloursContainerRef.current.getBoundingClientRect();
    const containerWidth = rect.width;
    const itemWidth = SWATCH_ITEM_SIZE + GAP_SIZE;
    const columns = Math.floor(
      (containerWidth + GAP_SIZE - INNER_WRAPPER_PADDING) / itemWidth,
    );

    const maxRows = Math.ceil(totalItems / columns);

    const requiredRow = Math.floor(selectedColourIndex / columns) + 1;
    const maxRowsToDisplay = isMobile ? 1 : 3;
    const rows = Math.max(maxRowsToDisplay, requiredRow);

    setHideToggleButton(rows === maxRows || maxRows < maxRowsToDisplay);

    setVisibleItemCount(Math.min(rows * columns, totalItems));
  };

  const handleResize = debounce(() => {
    recalculateRows();
  }, 200);

  useEffect(() => {
    recalculateRows();
  }, [selectedColourIndex, isMobile]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const toggleShowMore = () => {
    setIsExpanded((prev) => !prev);
    if (visibleItemCount) {
      coloursContainerRef.current
        ?.querySelectorAll("a")
        [visibleItemCount - 1]?.focus(); // Set focus on the last visible item
    }
    dispatchMonetateQEvent(COLOUR_SELECTOR_SHOW_MORE_BUTTON_CLICK);
  };

  const handleSkipLink = () => {
    setSkipLinkTargetVisible(true);
    setTimeout(() => {
      skipLinkTargetRef.current?.focus();
    }, 0);
  };

  return (
    <section className={styles.swatchSection}>
      <h5 className={styles.visuallyHidden}>Colour swatches</h5>
      <p className={styles.visuallyHidden}>
        In this section there is a list of {totalItems} colour swatches, which
        are an alternative way to explore the colour range. If you wish to skip
        the swatches and go to the end of the list, please use the following
        link.
      </p>
      <a
        className={styles.skipLink}
        href="#colour-list-end"
        onClick={handleSkipLink}
      >
        Skip the colour swatches
      </a>
      <ul
        className={cx("list")}
        ref={coloursContainerRef}
        role="listbox"
        aria-label="Colour selection swatches"
      >
        {colourOptions.map((colourOption, idx) => (
          <li
            ref={idx === selectedColourIndex ? activeElementRef : undefined}
            key={colourOption.colour}
            className={cx("item", {
              hidden:
                visibleItemCount && !isExpanded
                  ? idx >= visibleItemCount
                  : false,
            })}
            onClick={handleColourSelectClick}
          >
            <Swatch
              colour={colourOption}
              isDior={isDior}
              colourIndex={idx}
              replaceHistoryEntryOnVariantChange={
                replaceHistoryEntryOnVariantChange
              }
            />
          </li>
        ))}
      </ul>
      {!hideToggleButton && visibleItemCount !== undefined && (
        <TertiaryButton
          onClick={toggleShowMore}
          small
          IconComponent={isExpanded ? MinusOutlined : PlusOutlined}
          iconPositionRight
          className={cx("toggleButton")}
          aria-expanded={isExpanded}
          aria-controls="colour-list-grid"
        >
          {isExpanded
            ? "Show less"
            : `Show ${colourOptions.length - visibleItemCount} more`}
        </TertiaryButton>
      )}
      {skipLinkTargetVisible && (
        <p
          className={styles.visuallyHidden}
          ref={skipLinkTargetRef}
          tabIndex={-1}
          onBlur={() => setSkipLinkTargetVisible(false)}
          id="colour-list-end"
        >
          End of secondary colour menu
        </p>
      )}
    </section>
  );
};
